import { useState, useRef, useEffect } from 'react';
import Draggable from 'react-draggable';
import { FaTimes, FaMinus, FaRegSquare } from 'react-icons/fa';

export const Window = ({
  id,
  title,
  icon,
  children,
  isActive,
  isMinimized,
  isMaximized,
  onClose,
  onMinimize,
  onMaximize,
  onFocus,
  zIndex,
  defaultWidth = 800,
  defaultHeight = 600,
}) => {
  const [size, setSize] = useState({ width: defaultWidth, height: defaultHeight });
  const [position, setPosition] = useState({ x: 50, y: 30 });
  const [isResizing, setIsResizing] = useState(false);
  const windowRef = useRef(null);
  const nodeRef = useRef(null);

  const handleResize = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = size.width;
    const startHeight = size.height;

    const onMouseMove = (moveEvent) => {
      let newWidth = startWidth;
      let newHeight = startHeight;

      if (direction.includes('e')) {
        newWidth = Math.max(400, startWidth + (moveEvent.clientX - startX));
      }
      if (direction.includes('s')) {
        newHeight = Math.max(300, startHeight + (moveEvent.clientY - startY));
      }

      setSize({ width: newWidth, height: newHeight });
    };

    const onMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  if (isMinimized) return null;

  const windowStyle = isMaximized
    ? {
        width: '100%',
        height: 'calc(100vh - 40px)',
        top: 0,
        left: 0,
      }
    : {
        width: size.width,
        height: size.height,
      };

  return (
    <Draggable
      handle=".window-title-bar"
      disabled={isMaximized}
      nodeRef={nodeRef}
      defaultPosition={position}
      onStart={onFocus}
    >
      <div
        ref={nodeRef}
        className={`xp-window ${isActive ? 'active' : 'inactive'} ${isMaximized ? 'maximized' : ''}`}
        style={{ ...windowStyle, zIndex }}
        onClick={onFocus}
        data-testid={`window-${id}`}
      >
        {/* Title Bar */}
        <div className={`window-title-bar ${isActive ? 'active' : ''}`}>
          <div className="title-left">
            {icon && <span className="window-icon">{icon}</span>}
            <span className="window-title">{title}</span>
          </div>
          <div className="title-buttons">
            <button
              className="title-btn minimize"
              onClick={onMinimize}
              data-testid={`window-${id}-minimize`}
              aria-label="Minimize"
            >
              <FaMinus size={10} />
            </button>
            <button
              className="title-btn maximize"
              onClick={onMaximize}
              data-testid={`window-${id}-maximize`}
              aria-label="Maximize"
            >
              <FaRegSquare size={10} />
            </button>
            <button
              className="title-btn close"
              onClick={onClose}
              data-testid={`window-${id}-close`}
              aria-label="Close"
            >
              <FaTimes size={12} />
            </button>
          </div>
        </div>

        {/* Window Content */}
        <div className="window-content" ref={windowRef}>
          {children}
        </div>

        {/* Resize Handles */}
        {!isMaximized && (
          <>
            <div
              className="resize-handle resize-e"
              onMouseDown={(e) => handleResize(e, 'e')}
            />
            <div
              className="resize-handle resize-s"
              onMouseDown={(e) => handleResize(e, 's')}
            />
            <div
              className="resize-handle resize-se"
              onMouseDown={(e) => handleResize(e, 'se')}
            />
          </>
        )}
      </div>
    </Draggable>
  );
};

export default Window;
